/**
 * batch 계획기 (TASK-2026-004 SPEC 「포함 범위」 4, 중앙 명세 F1-05·F1-06).
 *
 * **순수 함수다.** 저장소도 `fetch`도 시각도 없다. 무엇을 한 묶음으로 보낼지만 정한다.
 * 실제 전송·재시도·ACK 정리는 `sender.ts`가 한다.
 *
 * ## 규칙
 *
 * - 이벤트는 ACK된 순번(`through_seq`) 다음부터, **끊김 없이 이어지는 구간만** 담는다.
 *   서버 계약이 batch 안의 순번 연속을 요구한다(`batch_seq_not_contiguous`). batch
 *   사이의 공백은 최종 제출이 판정한다.
 * - `batch_id`는 내용의 SHA-256에서 유도한다. 같은 내용을 다시 보내면 같은 ID이므로
 *   서버가 기존 ACK를 그대로 돌려주고(멱등), 다른 내용이 같은 ID를 쓰는 일이 없다.
 * - 상한(64KiB·이벤트 100·조각 100·확인 기록 100)을 넘기면 **보내기 전에** 줄인다.
 *   413을 받고 나서 줄이면 그 요청은 이미 실패로 남고, 대기열이 같은 실패를 반복한다.
 * - 아무것도 없으면 `empty`다. 빈 batch를 만들어 서버에 400을 받지 않는다.
 * - 한 건도 상한에 들어가지 않으면 `oversize`다. 성공으로 바꾸지 않고 사실대로 알린다.
 */

import type { PathChunk } from '../app/attempt';
import { canonicalize, sha256Hex } from '../telemetry/canonical';
import type { SaveConfirmation } from '../telemetry/confirmation';
import type { TelemetryEvent } from '../telemetry/events';

export const BATCH_SCHEMA = 'ccatlas.batch/1';

/** 서버 `limits.ts`와 같은 값이다. 여기서 바꾸면 계약이 깨진다. */
export const MAX_BATCH_BYTES = 64 * 1024;
export const MAX_BATCH_EVENTS = 100;
export const MAX_BATCH_CHUNKS = 100;
export const MAX_BATCH_CONFIRMATIONS = 100;

export interface ClientIntegrity {
  readonly complete: boolean;
  readonly dropped: number;
  readonly rejected: number;
  readonly invalid: number;
  readonly sequence_gaps: number;
  readonly sequence_conflicts: number;
  readonly unsequenced: number;
  /** 세션 누적 확인 기록 손실 수. `confirmations_state`와 묶음이다(CR-01 A+ §2.3). */
  readonly confirmations_dropped?: number;
  readonly confirmations_state?: ConfirmationsState;
}

export type ConfirmationsState = 'complete' | 'loss' | 'unverified';

/** batch가 서버에 보고하는 확인 기록 무결성 한 쌍 (R4-REV-001). */
export interface ConfirmationIntegrity {
  readonly confirmations_dropped: number;
  readonly confirmations_state: ConfirmationsState;
}

const SEVERITY: Readonly<Record<ConfirmationsState, number>> = { complete: 0, loss: 1, unverified: 2 };

/** `client_integrity`에서 확인 기록 무결성 쌍만 꺼낸다. 없으면 `null`. */
export function confirmationIntegrityOf(integrity: ClientIntegrity | null): ConfirmationIntegrity | null {
  if (integrity === null || integrity.confirmations_state === undefined || integrity.confirmations_dropped === undefined) {
    return null;
  }
  return { confirmations_dropped: integrity.confirmations_dropped, confirmations_state: integrity.confirmations_state };
}

/**
 * `current`가 서버가 이미 받은 `acked`보다 나쁜가 (R4-REV-001).
 *
 * 상태가 더 나쁘거나 손실 수가 늘었으면 새로 보고할 사실이 있다. `acked`가 없으면 기본값
 * `complete/0`을 받은 것으로 본다 —— 기본값은 보고할 사실이 아니다.
 */
export function integrityWorse(current: ConfirmationIntegrity, acked: ConfirmationIntegrity | null): boolean {
  const base = acked ?? { confirmations_dropped: 0, confirmations_state: 'complete' };
  return (
    SEVERITY[current.confirmations_state] > SEVERITY[base.confirmations_state] ||
    current.confirmations_dropped > base.confirmations_dropped
  );
}

export interface PathChunkPayload {
  readonly attempt_id: string;
  readonly index: number;
  readonly path: string;
  readonly deltas_ms: readonly number[];
  readonly truncated: boolean;
}

export interface BatchBody {
  readonly schema: typeof BATCH_SCHEMA;
  readonly batch_id: string;
  readonly events: readonly TelemetryEvent[];
  readonly path_chunks: readonly PathChunkPayload[];
  readonly stage_hashes: Readonly<Record<string, string>>;
  readonly confirmations: readonly SaveConfirmation[];
  readonly client_integrity: ClientIntegrity | null;
}

export interface PlannedBatch {
  readonly batchId: string;
  /** 서버에 그대로 보낼 canonical 문자열. */
  readonly text: string;
  readonly firstSeq: number | undefined;
  readonly lastSeq: number | undefined;
  readonly eventCount: number;
  /** 이 batch가 담은 조각 키. ACK 뒤 정리 대상이다. */
  readonly chunkKeys: readonly string[];
  readonly confirmationCount: number;
  /** 이 batch가 실은 확인 기록 무결성. 두 필드가 없는 batch면 `null`. ACK 뒤 보고 완료 표식이 된다. */
  readonly integrity: ConfirmationIntegrity | null;
  readonly bytes: number;
}

export type Plan =
  | { readonly kind: 'batch'; readonly batch: PlannedBatch }
  | { readonly kind: 'empty' }
  /** 한 건도 상한에 들어가지 않는다. 무엇이 문제인지 알린다. */
  | { readonly kind: 'oversize'; readonly bytes: number };

export interface PlanInput {
  /** 계측 로그 전체. 순번이 없는 기록은 보내지 않는다. */
  readonly events: readonly TelemetryEvent[];
  /** 서버가 연속 수신을 ACK한 마지막 순번. */
  readonly throughSeq: number;
  readonly chunks: readonly PathChunk[];
  readonly ackedChunkKeys: ReadonlySet<string>;
  readonly confirmations: readonly SaveConfirmation[];
  readonly integrity: ClientIntegrity | null;
  /** 서버가 ACK한 batch가 마지막으로 보고한 확인 기록 무결성. 없으면 기본값을 받은 것으로 본다. */
  readonly lastAckedIntegrity?: ConfirmationIntegrity | null;
  /** 등록된 stage의 content hash 전부. 결과 판정이 stage마다 hash를 요구한다. */
  readonly stageHashes: Readonly<Record<string, string>>;
}

export function chunkKey(attemptId: string, index: number): string {
  return `${attemptId}:${String(index)}`;
}

function toPayload(chunk: PathChunk): PathChunkPayload {
  return {
    attempt_id: chunk.attemptId,
    index: chunk.index,
    path: chunk.path,
    deltas_ms: [...chunk.deltasMs],
    truncated: chunk.truncated,
  };
}

/**
 * 아직 보내지 않은 이벤트에서 연속 구간을 고른다.
 *
 * 순번이 없는 기록(옛 로그)은 보내지 않는다. 서버 계약이 1 이상의 순번을 요구하고,
 * 순번 없는 기록은 어디에 끼워야 할지 정할 수 없다.
 */
function selectEvents(events: readonly TelemetryEvent[], throughSeq: number): readonly TelemetryEvent[] {
  const pending = events
    .filter((event): event is TelemetryEvent & { seq: number } => typeof event.seq === 'number' && event.seq > throughSeq)
    .sort((a, b) => a.seq - b.seq);

  const run: TelemetryEvent[] = [];
  for (const event of pending) {
    const previous = run.at(-1);
    if (previous === undefined) {
      run.push(event);
      continue;
    }
    const previousSeq = previous.seq ?? 0;
    if (event.seq === previousSeq) {
      // 같은 순번이 두 번 있으면 뒤의 것을 버린다. 서버는 내용이 다르면 충돌로 본다.
      continue;
    }
    if (event.seq !== previousSeq + 1 || run.length >= MAX_BATCH_EVENTS) {
      break;
    }
    run.push(event);
  }
  return run;
}

/** stage hash는 이 batch가 실제로 언급한 stage만 싣는다(상한 16개). */
function hashesFor(
  events: readonly TelemetryEvent[],
  all: Readonly<Record<string, string>>,
): Record<string, string> {
  const used = new Set<string>();
  for (const event of events) {
    const stageId = event.payload['stage_id'];
    if (typeof stageId === 'string') {
      used.add(stageId);
    }
  }
  const hashes: Record<string, string> = {};
  for (const stageId of [...used].sort()) {
    const hash = all[stageId];
    if (hash !== undefined) {
      hashes[stageId] = hash;
    }
  }
  return hashes;
}

function build(
  events: readonly TelemetryEvent[],
  chunks: readonly PathChunk[],
  confirmations: readonly SaveConfirmation[],
  input: PlanInput,
): PlannedBatch {
  const body = {
    schema: BATCH_SCHEMA,
    events,
    path_chunks: chunks.map(toPayload),
    stage_hashes: hashesFor(events, input.stageHashes),
    confirmations,
    client_integrity: input.integrity,
  };
  /*
   * ID는 내용에서 나온다. `batch_id`를 뺀 본문의 canonical hash를 쓰므로 같은 내용은
   * 항상 같은 ID이고, 내용이 한 글자라도 다르면 ID도 다르다.
   */
  const batchId = `b${sha256Hex(canonicalize(body)).slice(0, 31)}`;
  const text = canonicalize({ ...body, batch_id: batchId });
  return {
    batchId,
    text,
    firstSeq: events[0]?.seq,
    lastSeq: events.at(-1)?.seq,
    eventCount: events.length,
    chunkKeys: chunks.map((chunk) => chunkKey(chunk.attemptId, chunk.index)),
    confirmationCount: confirmations.length,
    integrity: confirmationIntegrityOf(input.integrity),
    bytes: new TextEncoder().encode(text).length,
  };
}

export function planBatch(input: PlanInput): Plan {
  let events = selectEvents(input.events, input.throughSeq);
  let chunks = input.chunks
    .filter((chunk) => !input.ackedChunkKeys.has(chunkKey(chunk.attemptId, chunk.index)))
    .slice(0, MAX_BATCH_CHUNKS);
  let confirmations = input.confirmations.slice(0, MAX_BATCH_CONFIRMATIONS);

  if (events.length === 0 && chunks.length === 0 && confirmations.length === 0) {
    /*
     * 콘텐츠가 없어도 **아직 서버에 보고하지 않은 손실·확인 불가**가 있으면 checkpoint batch로
     * 보낸다 (R4-REV-001). 마지막 데이터 batch 뒤에 spool 상태만 나빠지면, 그 사실을 실을
     * batch가 없어 확정 결과에서 빠진다. 기본값(`complete/0`)만 있으면 빈 batch를 만들지 않는다.
     */
    const current = confirmationIntegrityOf(input.integrity);
    if (
      current === null ||
      current.confirmations_state === 'complete' ||
      !integrityWorse(current, input.lastAckedIntegrity ?? null)
    ) {
      return { kind: 'empty' };
    }
    return { kind: 'batch', batch: build([], [], [], input) };
  }

  let planned = build(events, chunks, confirmations, input);
  while (planned.bytes > MAX_BATCH_BYTES) {
    /*
     * 줄이는 순서: 확인 기록 → 조각 → 이벤트. 확인 기록은 저장 판정의 관찰일 뿐이라
     * 늦게 가도 되고, 이벤트와 조각은 최종 제출의 판정에 필요하다.
     */
    if (confirmations.length > 0) {
      confirmations = confirmations.slice(0, Math.floor(confirmations.length / 2));
    } else if (chunks.length > 1) {
      chunks = chunks.slice(0, Math.floor(chunks.length / 2));
    } else if (events.length > 1) {
      events = events.slice(0, Math.floor(events.length / 2));
    } else if (chunks.length === 1 && events.length === 1) {
      // 둘 다 한 건이면 이벤트만 남긴다. 조각은 다음 batch에서 혼자 간다.
      chunks = [];
    } else {
      return { kind: 'oversize', bytes: planned.bytes };
    }
    if (events.length === 0 && chunks.length === 0 && confirmations.length === 0) {
      return { kind: 'oversize', bytes: planned.bytes };
    }
    planned = build(events, chunks, confirmations, input);
  }
  return { kind: 'batch', batch: planned };
}
